/** Cryptographically secure random integer in [0, max) without modulo bias. */
export function randInt(max: number): number {
  if (max <= 0) return 0;
  const limit = Math.floor(0x100000000 / max) * max;
  const buf = new Uint32Array(1);
  do { crypto.getRandomValues(buf); } while (buf[0] >= limit);
  return buf[0] % max;
}

export function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) { const j = randInt(i + 1); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

export interface PasswordOptions { length: number; upper: boolean; lower: boolean; numbers: boolean; symbols: boolean; excludeSimilar: boolean; symbolSet: string }
export const DEFAULT_SYMBOLS = "!@#$%^&*()_+-=[]{}|;:,.<>?";
const SIMILAR = /[Il1O0o|]/g;

export function poolsFor(o: PasswordOptions): string[] {
  const strip = (s: string) => (o.excludeSimilar ? s.replace(SIMILAR, "") : s);
  const pools: string[] = [];
  if (o.upper) pools.push(strip("ABCDEFGHIJKLMNOPQRSTUVWXYZ"));
  if (o.lower) pools.push(strip("abcdefghijklmnopqrstuvwxyz"));
  if (o.numbers) pools.push(strip("0123456789"));
  if (o.symbols) pools.push(strip(Array.from(new Set(o.symbolSet || DEFAULT_SYMBOLS)).join("")));
  return pools.filter(Boolean);
}

/** The distinct characters a password can use (a symbol that is also a letter is counted once). */
export function alphabetFor(o: PasswordOptions): string[] {
  return Array.from(new Set(poolsFor(o).join("")));
}

/**
 * Every selected class appears at least once (sites that demand a digit and a symbol always accept it).
 * Done by drawing the whole password uniformly from the alphabet and redrawing when a class is missing, so the result is
 * UNIFORM over all valid passwords. That is what makes the entropy shown (log2 of the number of valid passwords) exact,
 * rather than a figure for a shortcut that favours some passwords. Redraws are cheap: even at length 4 with four classes
 * about 1 draw in 14 is accepted, and at 16 characters nearly all are.
 */
export function generatePassword(o: PasswordOptions): string {
  const pools = poolsFor(o);
  if (!pools.length) return "";
  const alphabet = alphabetFor(o);
  const sets = pools.map((p) => new Set(p));
  if (o.length < pools.length) return Array.from({ length: o.length }, () => alphabet[randInt(alphabet.length)]).join("");
  for (let tries = 0; tries < 1_000_000; tries++) {
    const chars = Array.from({ length: o.length }, () => alphabet[randInt(alphabet.length)]);
    if (sets.every((s) => chars.some((c) => s.has(c)))) return chars.join("");
  }
  throw new Error("Could not generate a password with these settings.");
}

const ZERO = BigInt(0), ONE = BigInt(1);
const bigPow = (base: number, exp: number): bigint => { let r = ONE; const b = BigInt(base); for (let i = 0; i < exp; i++) r *= b; return r; };
/** log2 of a positive BigInt, accurate to double precision. */
export function log2Big(n: bigint): number {
  if (n <= ZERO) return 0;
  const s = n.toString(2);
  if (s.length <= 53) return Math.log2(Number(n));
  return Math.log2(Number(BigInt("0b" + s.slice(0, 53)))) + (s.length - 53);
}

/** How many different passwords these options can produce: strings of the length over the alphabet that use every selected class (inclusion-exclusion over the classes left out). */
export function validPasswordCount(o: PasswordOptions): bigint {
  const pools = poolsFor(o);
  const N = alphabetFor(o).length;
  if (!pools.length || N < 1 || o.length < pools.length) return ZERO;
  let total = ZERO;
  for (let mask = 0; mask < 1 << pools.length; mask++) {
    const out = new Set<string>(); let bits = 0;
    pools.forEach((p, i) => { if (mask & (1 << i)) { bits++; for (const c of p) out.add(c); } });
    const term = bigPow(N - out.size, o.length);
    total += bits % 2 === 0 ? term : -term;
  }
  return total;
}

export function entropyBits(poolSize: number, length: number): number {
  return poolSize > 1 ? length * Math.log2(poolSize) : 0;
}

/** Entropy of the options a password was actually generated with: log2 of the number of valid passwords (0 when no class is selected). */
export function optionsEntropyBits(o: PasswordOptions): number {
  return log2Big(validPasswordCount(o));
}

export interface EntropyDetail { alphabetSize: number; naiveBits: number; bits: number; lines: string[] }
/** The maths shown beside a password, from the same numbers the strength readout uses. */
export function passwordEntropyDetail(o: PasswordOptions): EntropyDetail {
  const N = alphabetFor(o).length;
  const naive = entropyBits(N, o.length), bits = optionsEntropyBits(o);
  const lines = [`${o.length} characters, each one of ${N} possible characters: ${o.length} x log2(${N}) = ${naive.toFixed(1)} bits.`];
  if (naive - bits >= 0.05) lines.push(`Requiring every chosen type to appear rules out some passwords, so the exact figure is ${bits.toFixed(1)} bits (log2 of the ${validPasswordCount(o).toLocaleString("en-US")} passwords that qualify).`);
  return { alphabetSize: N, naiveBits: naive, bits, lines };
}

export function passphraseEntropyDetail(wordCount: number, addNumber: boolean): EntropyDetail {
  const words = entropyBits(7776, wordCount), num = addNumber ? Math.log2(100 * wordCount) : 0;
  const lines = [`${wordCount} words, each one of 7,776 on the EFF list: ${wordCount} x log2(7,776) = ${words.toFixed(1)} bits.`];
  if (addNumber) lines.push(`The two-digit number goes after a random word: ${wordCount} places x 100 numbers, log2(${100 * wordCount}) = ${num.toFixed(1)} bits.`);
  lines.push("Capital letters and the separator are fixed by your choice, so they add no bits.");
  return { alphabetSize: 7776, naiveBits: words + num, bits: words + num, lines };
}

export function passphraseEntropyBits(wordCount: number, addNumber: boolean): number {
  return entropyBits(7776, wordCount) + (addNumber ? Math.log2(100 * wordCount) : 0);
}

/** The attacker the crack-time figure imagines: 10 billion guesses a second, an offline attack on a password stored with a fast hash. */
export const GUESSES_PER_SECOND = 1e10;
export const CRACK_ASSUMPTION = "Assumes an attacker trying 10 billion guesses a second against a fast-hashed copy of the password and getting there after trying half of all possibilities. A site that stores passwords with a slow hash (bcrypt, Argon2) is far slower to attack; a site that leaks them in plain text makes this number meaningless. It holds only for a password drawn at random like this one: a password you edit, reuse or recognise from a breach is not covered.";

export function crackTime(bits: number): string {
  // Average case is half the space.
  const seconds = Math.pow(2, bits - 1) / GUESSES_PER_SECOND;
  if (seconds < 1) return "less than a second";
  // Past a trillion years the exact figure means nothing; say what it is compared with instead of printing a silly number.
  if (seconds >= 31557600 * 1e12) return "over a trillion years (the universe is about 13.8 billion years old)";
  const units: [number, string][] = [[31557600 * 1e9, "billion years"], [31557600 * 1e6, "million years"], [31557600 * 1e3, "thousand years"], [31557600, "years"], [86400, "days"], [3600, "hours"], [60, "minutes"], [1, "seconds"]];
  for (const [s, name] of units) if (seconds >= s) { const v = seconds / s; return `about ${v >= 100 ? Math.round(v).toLocaleString("en-US") : v.toFixed(v >= 10 ? 0 : 1)} ${name}`; }
  return "seconds";
}

export function strengthLabel(bits: number): { label: string; color: string } {
  if (bits >= 100) return { label: "Very Strong", color: "text-green-600" };
  if (bits >= 70) return { label: "Strong", color: "text-green-500" };
  if (bits >= 45) return { label: "Medium", color: "text-yellow-500" };
  return { label: "Weak", color: "text-red-500" };
}

export function generatePassphrase(words: string[], count: number, separator: string, capitalize: boolean, addNumber: boolean): string {
  const picked = Array.from({ length: count }, () => words[randInt(words.length)]).map((w) => (capitalize ? w[0].toUpperCase() + w.slice(1) : w));
  if (addNumber) picked[randInt(picked.length)] += String(randInt(100)).padStart(2, "0");
  return picked.join(separator);
}
